import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { applyTheme, getInitialTheme, subscribeSystemTheme, type Theme } from '@/lib/theme';

/**
 * ThemeContext — Phase 5 (full dark theme support).
 *
 * Theme detection priority:
 *  1. MAX SDK `bridge.colorScheme` (на Android/iOS WebView).
 *  2. window.matchMedia (prefers-color-scheme: dark).
 *  3. 'light' (SSR / unsupported).
 *
 * Runtime behavior:
 *  - На mount берёт initial theme и применяет <html data-theme="...">.
 *  - Подписывается на matchMedia changes — если пользователь меняет
 *    системную тему во время сессии, Mini App переключается live.
 *  - setTheme(...) — explicit override, useful для DevToolbar / future
 *    user toggle. Не persist'ится: следующая загрузка снова system-driven.
 *  - toggleTheme() — flip light↔dark.
 *
 * No localStorage persistence в v1: тема следует системе при каждой
 * загрузке. Простая модель без surprises.
 */

export interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme());
  const [userOverridden, setUserOverridden] = useState(false);

  // Apply theme to <html data-theme> on every change.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Subscribe to system theme changes. Auto-update unless user has
  // explicitly overridden (e.g. via DevToolbar).
  useEffect(() => {
    const unsubscribe = subscribeSystemTheme((systemTheme) => {
      if (!userOverridden) {
        setThemeState(systemTheme);
      }
    });
    return unsubscribe;
  }, [userOverridden]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (next) => {
        setUserOverridden(true);
        setThemeState(next);
      },
      toggleTheme: () => {
        setUserOverridden(true);
        setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
      },
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to read current theme. Returns 'light' fallback если вызывается
 * вне ThemeProvider (defensive, не должно случаться в продакшене —
 * App.tsx wrap'ает всё).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: 'light',
      setTheme: () => undefined,
      toggleTheme: () => undefined,
    };
  }
  return ctx;
}
